import { listen } from "@tauri-apps/api/event";
import { getCurrentWindow } from "@tauri-apps/api/window";
import {
  isNativeGlassUnsupported,
  NATIVE_GLASS_REFRESH_EVENT,
} from "./native-glass-settings";
import { safeTauriUnlisten } from "./tauri-listener-cleanup";
import { NATIVE_GLASS_FALLBACK_SETTLED } from "./native-glass-fallback";

const NOT_SETTLED = "Settings could not yield native focus.";

let cancelPending: (() => void) | null = null;
export function cancelNativeGlassSettled(): void {
  cancelPending?.();
}
export function nativeGlassSettledTrace(detail: unknown): string | null {
  const trace = (detail as { settledTraceId?: unknown } | null)?.settledTraceId;
  return typeof trace === "string" && /^yield-[\da-f-]{36}$/.test(trace)
    ? trace
    : null;
}

/** A queued IPC command is insufficient: wait for the native applied receipt. */
export async function requestNativeGlassSettled(
  signal: AbortSignal,
  { allowCssFallback = false }: { allowCssFallback?: boolean } = {},
): Promise<void> {
  cancelPending?.();
  // No renderer collects any more once native glass is unsupported: CSS
  // already paints every surface, so there is no native receipt to wait for.
  if (isNativeGlassUnsupported()) {
    if (allowCssFallback) return;
    throw new Error(NOT_SETTLED);
  }
  const traceId = `yield-${crypto.randomUUID()}`;
  let settled = false;
  let finish!: (ok: boolean) => void;
  const result = new Promise<void>((resolve, reject) => {
    finish = (ok) => {
      if (settled) return;
      settled = true;
      if (ok) resolve();
      else reject(new Error(NOT_SETTLED));
    };
  });
  const cancel = () => finish(false);
  const fallback = (event: Event) => {
    if (
      allowCssFallback &&
      (event as CustomEvent<{ traceId?: string }>).detail?.traceId === traceId
    )
      finish(true);
  };
  cancelPending = cancel;
  const timer = window.setTimeout(cancel, 8000);
  signal.addEventListener("abort", cancel, { once: true });
  window.addEventListener(NATIVE_GLASS_FALLBACK_SETTLED, fallback);
  const registration = listen<{ traceId?: string; stage?: string }>(
    "denker://native-glass-applied",
    ({ payload }) => {
      if (payload.traceId === traceId)
        finish(payload.stage === "applied" || payload.stage === "cleared");
    },
    { target: { kind: "WebviewWindow", label: getCurrentWindow().label } },
  );
  // Install the receipt listener before asking the renderer to collect hidden Settings.
  void registration.then(() => {
    if (settled) return;
    if (signal.aborted) return cancel();
    window.dispatchEvent(
      new CustomEvent(NATIVE_GLASS_REFRESH_EVENT, {
        detail: {
          forceSend: true,
          trackTransition: false,
          settledTraceId: traceId,
        },
      }),
    );
  }, cancel);
  try {
    await result;
  } finally {
    window.clearTimeout(timer);
    signal.removeEventListener("abort", cancel);
    window.removeEventListener(NATIVE_GLASS_FALLBACK_SETTLED, fallback);
    if (cancelPending === cancel) cancelPending = null;
    void registration.then(safeTauriUnlisten).catch(() => {});
  }
}
